import {
  Auth0Client,
  GetIdTokenClaimsOptions,
  GetTokenSilentlyOptions,
  IdToken,
  LogoutOptions,
  TokenEndpointResponse,
} from '@auth0/auth0-spa-js';
import {
  Auth0ClientAdapter,
  auth0ClientAdapterLoader,
} from '../Auth0ClientAdapter';
import {
  NativeAuthClientAdapter,
  nativeAuthClientAdapterLoader,
} from '../NativeAuthClientAdapter';
import createCache from '../utils/cache';

export interface AuthPageParams {
  // using snakecase properties here to keep SSO page (and OAuth2) convention
  /* eslint-disable camelcase */

  /**
   * - `'team_auth'`: show auth page for team management
   * - `'team_auth'`: show auth page for account management
   * - `'team_invite'`: show auth page for a user accepting an invite to a team.
   *   `team_name` and `login_hint` should be included
   */
  account_action?: 'team_invite' | 'team_auth' | 'account_auth';

  /**
   * name of the team to display on the auth page, applicable when
   * `account_action=team_invite`
   */
  team_name?: string;
  /**
   * The user's email address. When your app knows
   * which user is trying to authenticate, you can provide this parameter
   * to pre-fill the email box or select the right session for sign-in.
   *
   * May be used in combination with `lock_email`.
   */
  login_hint?: string;
  /**
   * - `'login'`: prompt user for reauthentication
   */
  prompt?: 'login' | undefined;
  /**
   * To disable the email input on the authentication page. Only takes effect if
   * `login_hint` is provided.
   *
   * Note: this only locks the input for users that will enter email/password.
   * This will not prevent users from authenticating with a social provider
   * using any account.
   */
  lock_email?: boolean;

  /**
   * The connection (Eg: google, apple, slack, etc) to be used while showing the login page
   */
  connection?: string;
  /* eslint-enable camelcase */

  /**
   * If you need to send custom parameters to the Authorization Server,
   * make sure to use the original parameter name.
   */
  [key: string]: any;
}

export interface AuthWithRedirectParams {
  /**
   * language to use on the authentication screen
   *
   * @example "en"
   */
  language?: string;
  /**
   * search string to append to the redirect uri
   * This should not be filled with dynamic values, everything added to the
   * `redirect_uri` of your application should included as an allowed url in the
   * Auth0 setup.
   *
   * Instead use `appState` for handling more dynamic scenarios when a user
   * returns
   */
  search?: string;
  /**
   * used to store state before redirecting the user away. This state will be
   * available when the user returns after authenticating.
   */
  appState?: Record<string, unknown>;
  /**
   * special parameters used by the authentication page to customize the UI
   */
  authPageParams?: AuthPageParams;
  /**
   * Absolute url where user will be redirected to after successful authentication
   */
  redirectUrl?: string;
}

export interface AdaptableAuthClient {
  checkSession: Auth0Client['checkSession'];
  isAuthenticated: Auth0Client['isAuthenticated'];
  getIdTokenClaims: Auth0Client['getIdTokenClaims'];
  getTokenSilently: (options?: GetTokenSilentlyOptions) => Promise<string>;
  login: (params: AuthWithRedirectParams) => Promise<void>;
  signUp: (params: AuthWithRedirectParams) => Promise<void>;
  logoutSession: (options?: LogoutOptions) => Promise<void>;
  forgotPassword: (params: AuthWithRedirectParams) => Promise<void>;
  clearLocalCache: () => void;
  storeTokens?: (
    authResult: TokenEndpointResponse
  ) => Promise<IdToken | undefined>;
}

/**
 * function which will return a singleton instance of the authentication client.
 */
export type AuthClientLoader<AuthClient extends AdaptableAuthClient> =
  () => AuthClient;

export interface AdaptableAuthClientLoaders {
  nativeAuthClient: AuthClientLoader<NativeAuthClientAdapter>;
  auth0AuthClient: AuthClientLoader<Auth0ClientAdapter>;
}

export interface AuthenticationAdapterOptions {
  clientOptions: {
    auth0ClientOptions: ConstructorParameters<typeof Auth0ClientAdapter>[0];
    nativeAuthOptions?: ConstructorParameters<typeof NativeAuthClientAdapter>;
  };
}

export class AuthenticationAdapter implements AdaptableAuthClient {
  private _activeAuthClient!: AuthClientLoader<AdaptableAuthClient>;
  private _authClientLoaders: AdaptableAuthClientLoaders;

  constructor({ clientOptions }: AuthenticationAdapterOptions) {
    const auth0AuthClientOptions = clientOptions.auth0ClientOptions;

    // Enabling refresh tokens by default unless false is passed explicitly by the clients
    auth0AuthClientOptions.useRefreshTokens =
      auth0AuthClientOptions.useRefreshTokens === false ? false : true;

    if (auth0AuthClientOptions.useRefreshTokens === true) {
      auth0AuthClientOptions.cache = createCache();
    }

    const nativeAuthClientOptions = clientOptions.nativeAuthOptions ?? {};
    this._authClientLoaders = {
      auth0AuthClient: auth0ClientAdapterLoader(auth0AuthClientOptions),
      nativeAuthClient: nativeAuthClientAdapterLoader(nativeAuthClientOptions),
    };
  }

  /**
   * sets the authentication client that should be used. Will not execute the
   * loader of the auth client until it is actually used.
   *
   * @param clientName
   */
  setActiveAuthClient(clientName: keyof AdaptableAuthClientLoaders): void {
    if (!this._authClientLoaders[clientName]) {
      throw new Error('Can not set the active client to one without a loader');
    }

    this._activeAuthClient = this._authClientLoaders[
      clientName
    ] as () => AdaptableAuthClient;
  }

  /**
   * For internal use, but exposed for specific scenarios when it is required to
   * work directly with the underlying authentication client
   *
   * Will execute the loader function of the auth client.
   * @internal
   */
  getActiveAuthClient(): AdaptableAuthClient {
    if (!this._activeAuthClient) {
      this.setActiveAuthClient('auth0AuthClient');
    }

    return this._activeAuthClient() as AdaptableAuthClient;
  }

  async checkSession(options?: GetTokenSilentlyOptions): Promise<void> {
    return this.getActiveAuthClient().checkSession(options);
  }

  async isAuthenticated(): Promise<boolean> {
    return this.getActiveAuthClient().isAuthenticated();
  }

  async getIdTokenClaims(
    options?: GetIdTokenClaimsOptions
  ): Promise<IdToken | undefined> {
    return this.getActiveAuthClient().getIdTokenClaims(options);
  }

  async getTokenSilently(options?: GetTokenSilentlyOptions): Promise<string> {
    return this.getActiveAuthClient().getTokenSilently(options);
  }

  async signUp(params: AuthWithRedirectParams): Promise<void> {
    return this.getActiveAuthClient().signUp(params);
  }

  async login(params: AuthWithRedirectParams): Promise<void> {
    return this.getActiveAuthClient().login(params);
  }

  async logoutSession(options?: LogoutOptions): Promise<void> {
    return this.getActiveAuthClient().logoutSession(options);
  }

  async forgotPassword(params: AuthWithRedirectParams): Promise<void> {
    return this.getActiveAuthClient().forgotPassword(params);
  }

  async storeTokens(
    authResult: TokenEndpointResponse
  ): Promise<IdToken | undefined> {
    return this.getActiveAuthClient().storeTokens?.(authResult);
  }

  clearLocalCache(): void {
    return this.getActiveAuthClient().clearLocalCache();
  }
}
